import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useAllBook, useBookOrder } from 'store/books/selectors';
import {
  handleCloneCustomAssignment,
  handleCloneWorksheetAssignmentToPage,
  handleUpdateCustomAssignment,
  handleListActivities,
  handleAdminCopyOrMoveAssignment,
} from 'store/activities/thunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import Loading from 'components/Loading';
import get from 'lodash/get';
import find from 'lodash/find';
import map from 'lodash/map';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentOrgPermission } from 'store/organizations/selectors';
import { useUserRole } from 'store/users/selectors';
import { dropdownForResourceType } from 'utils/config/reference';
import { resource_types } from 'store/extraResource/constants';
import { RESOURCE_BOOK_ID_MAP } from 'modules/assignments/helper';
import ResourceTypeSelector from 'modules/extraResource/admin/resourceTypeSelector';
import { handleListClassActivities } from 'store/classActivities/thunks';
import { handleListClassQuizzes } from 'store/classQuizzes/thunks';
import { useLocation } from 'react-router-dom';

export type CloneActivityAssignment = {
  id: string;
  chapter?: string | number;
  bookId?: string | number;
  page?: string | number;
  type?: string;
  typeId?: string;
  assignmentType?: string;
  displayName?: string;
  questions?: unknown;
  share_list?: unknown;
  share_by?: string;
  language_feedback?: unknown;
  ai_grading_config?: unknown;
};

type Mode = 'copy' | 'move';

type CloneActivityToPageModalProps = {
  open: boolean;
  onClose: () => void;
  assignment: CloneActivityAssignment | null;
  onSuccess?: (destChapter?: string | number) => void;
  is_assigned?: boolean;
  adminMode?: boolean;
};

const CloneActivityToPageModal = ({
  open,
  onClose,
  assignment,
  onSuccess,
  is_assigned=false,
  adminMode = false,
}: CloneActivityToPageModalProps) => {
  const showAlert = useAlert();
  const dispatch = useDispatch<any>();
  const booksAllList = useAllBook();
  const location = useLocation();
  const currentClass = useCurrentClass();
  const archived_group = useMemo(() => currentClass.archived_group || [], [currentClass]);
  const orgPermissions = useCurrentOrgPermission();
  const bookOrder = useBookOrder();
  const { is_super_admin, currentUser } = useUserRole();

  const sourceBookId = assignment?.chapter ?? assignment?.bookId;
  const sourcePage = assignment?.page;
  const contextPageId = assignment?.page;
  const getCurrentResourceType = () => {
    if (assignment?.type === 'story' || location.pathname === '/stories') {
      return resource_types.story;
    }
    if (location.pathname === '/video' || (is_super_admin && location.pathname === '/edit-resources')) {
      return resource_types.resource;
    }
    if (location.pathname === '/apBook' || (is_super_admin ? assignment?.type === 'apexam' : '')) {
      return resource_types.AP;
    }
    if (String(sourceBookId) === '43') {
      return resource_types.comunidad;
    }
    if (String(sourceBookId) === '49') {
      return resource_types.conectándome;
    }
    if (String(sourceBookId) === '33') {
      return resource_types.balcon;
    }
    if (String(sourceBookId) === '41') {
      return resource_types.informate;
    }
    return 'ebook';
  };

  const [cloneResourceType, setCloneResourceType] = useState(() => getCurrentResourceType());
  const [targetBook, setTargetBook] = useState<string | number>('');
  const [targetPage, setTargetPage] = useState<string | number>('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>('copy');
  const [selectedItems, setSelectedItems] = useState<unknown[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<Record<string, unknown>>({});
  const [selectedMaskId, setSelectedMaskId] = useState('');

  const isCustom = assignment?.type === 'custom' || assignment?.typeId === 'custom';
  const isWorksheetAssignment =
    assignment?.typeId === 'worksheet' || assignment?.assignmentType === 'worksheet';
  const effectiveMode: Mode = adminMode || isCustom || isWorksheetAssignment ? mode : 'copy';
  const currentResourceType = getCurrentResourceType();
  const orderedBook = useMemo(() => {
    let order_group: { key: string; title: string; color: string; books: unknown[] }[] = [];
    const books_group_permission = get(orgPermissions, 'books_group_permission', []);
    (bookOrder || []).forEach((order) => {
      const class_books_group = currentClass.books_group || [];
      if (archived_group.includes(order.key)) return null;
      if (books_group_permission.length == 0 || books_group_permission.includes(order.key)) {
        const has_book = find(class_books_group, (k) => k == order.key);
        if (has_book) {
          const result = [
            ...(order &&
            order.order &&
            order.order
              .map((chapter: string) => {
                const book = find(booksAllList, ['id', chapter]);
                if (book) {
                  return book;
                }
                return null;
              })
              .filter(Boolean)),
          ];
          order_group.push({
            key: order.key,
            title: order.title,
            color: order.color,
            books: result,
          });
        }
      }
    });
    return order_group;
  }, [currentClass, orgPermissions, booksAllList, archived_group, bookOrder]);

  const allowedBooks = useMemo((): { id: string | number }[] => {
    return Array.from(
      new Set(orderedBook.map((order) => order.books).flat())
    ) as { id: string | number }[];
  }, [orderedBook]);

  const availableBooksForTarget = useMemo(() => {
    const bks = Array.isArray(booksAllList) ? booksAllList : [];
    if (is_super_admin || adminMode) {
      return bks.filter((bk) => bk.isNotAssignable === false && bk.active === true);
    }
    return bks.filter((bk) => allowedBooks.find((alb) => alb.id == bk.id));
  }, [booksAllList, allowedBooks, is_super_admin, adminMode]);

  const canShowMoveOption =
    cloneResourceType === currentResourceType &&
    (cloneResourceType !== 'ebook' || !targetBook || String(targetBook) === String(sourceBookId));
  const dialogTitle = adminMode
    ? canShowMoveOption
      ? 'Copy or move (admin)'
      : 'Copy (admin)'
    : isCustom
      ? canShowMoveOption
        ? 'Copy or move activity'
        : 'Copy activity'
      : isWorksheetAssignment
        ? canShowMoveOption
          ? 'Copy or move grammar drill'
          : 'Copy grammar drill'
        : 'Copy activity';

  const resetEbookTarget = (nextMode: Mode) => {
    setTargetPage('');
    if (nextMode === 'move') {
      setTargetBook((assignment?.chapter || assignment?.bookId || '') as string | number);
      return;
    }
    setTargetBook('');
  };

  const pagesForTargetBook = useMemo(() => {
    if (!targetBook || !availableBooksForTarget.length) return [];
    const selectedBook = availableBooksForTarget.find((b) => b.id === targetBook);
    const pages = selectedBook?.pages || [];
    if (!Array.isArray(pages)) return [];
    return pages
      .map((p: unknown): { value: string | number; label: string } | null => {
        if (typeof p === 'number') {
          return { value: p, label: `Pg. ${p}` };
        }
        const pageObj = p as { pageId?: number; id?: number; NumberMasked?: string; displayName?: string };
        const idVal = pageObj.pageId ?? pageObj.id;
        if (!idVal && idVal !== 0) return null;
        const label = pageObj.NumberMasked || pageObj.displayName || `Pg. ${idVal}`;
        return { value: idVal as string | number, label };
      })
      .filter(
        (p): p is { value: string | number; label: string } =>
          p != null &&
          !(
            String(targetBook) === String(sourceBookId) &&
            String(p.value) === String(sourcePage)
          )
      );
  }, [availableBooksForTarget, targetBook, sourceBookId, sourcePage]);

  const isBookUnitType = () => {
    const { balcon, conectándome, comunidad, informate } = resource_types;
    return (
      cloneResourceType === balcon ||
      cloneResourceType === conectándome ||
      cloneResourceType === comunidad ||
      cloneResourceType === informate
    );
  };

  const needsMaskIdDropdown = () => {
    return (
      isBookUnitType() ||
      cloneResourceType === resource_types.AP ||
      cloneResourceType === resource_types.story
    );
  };

  const handleResourceTypeChange = (value: string) => {
    setCloneResourceType(value);
    setSelectedItems([]);
    setSelectedConfig({});
    setSelectedMaskId('');
    if (value === 'ebook') {
      resetEbookTarget(mode);
    } else {
      setTargetPage('');
      setTargetBook('');
    }
  };

  const handleModeChange = (value: Mode) => {
    setMode(value);
    if (cloneResourceType === 'ebook') {
      resetEbookTarget(value);
    }
  };

  React.useEffect(() => {
    if (!canShowMoveOption && mode === 'move') {
      setMode('copy');
    }
  }, [canShowMoveOption, mode]);

  const getBookIdForResourceType = (): string | number | null => {
    if (cloneResourceType === 'ebook') {
      return targetBook;
    }
    if (isBookUnitType()) {
      return RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
    }
    if (cloneResourceType === resource_types.AP && selectedConfig?.selectedCategoryAP) {
      const ap = selectedConfig.selectedCategoryAP as { chapter_ids?: string | number };
      return ap.chapter_ids ?? null;
    }
    if (cloneResourceType === resource_types.story && selectedItems && selectedItems.length > 0) {
      const storyItem = selectedItems[0] as { bookId?: string | number };
      if (storyItem.bookId) {
        return storyItem.bookId;
      }
    }
    if (cloneResourceType === resource_types.resource && selectedItems && selectedItems.length > 0) {
      const r = selectedItems[0] as { id?: string | number };
      return r.id ?? null;
    }
    if (selectedItems && selectedItems.length > 0) {
      const firstItem = selectedItems[0] as { bookId?: string | number };
      if (firstItem.bookId) {
        return firstItem.bookId;
      }
    }
    return RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
  };

  const getMaskIdFromSelection = (): string | number | null => {
    if (cloneResourceType === 'ebook') {
      return targetPage;
    }
    if (needsMaskIdDropdown() && selectedMaskId) {
      return selectedMaskId;
    }
    if (cloneResourceType === resource_types.resource && selectedItems && selectedItems.length > 0) {
      const r = selectedItems[0] as { id?: string | number };
      return r.id ?? null;
    }
    if (selectedItems && selectedItems.length > 0) {
      const firstItem = selectedItems[0] as { id?: string | number; pageId?: string | number };
      return firstItem.id ?? firstItem.pageId ?? null;
    }
    return null;
  };

  const getMaskIdOptionsForSelectedCover = useMemo(() => {
    if (!needsMaskIdDropdown() || !selectedItems || selectedItems.length === 0) {
      return [];
    }

    let startPage: number | null = null;
    let finishPage: number | null = null;
    let maskBookId: string | number | null = null;

    if (cloneResourceType === resource_types.story) {
      const storyItem = selectedItems[0] as {
        bookId?: string | number;
        levels?: Array<{ level?: string; start?: string; finish?: string }>;
      };
      const configLevel = selectedConfig?.level as string | undefined;
      const levels = storyItem?.levels;

      if (!configLevel || !levels || !Array.isArray(levels)) {
        return [];
      }

      const matchedLevel = levels.find((l) => {
        const levelName = l?.level?.toLowerCase().trim();
        const configLevelName = configLevel?.toLowerCase().trim();
        return levelName === configLevelName;
      });

      if (!matchedLevel || !matchedLevel.start || !matchedLevel.finish) {
        return [];
      }

      startPage = parseInt(matchedLevel.start, 10);
      finishPage = parseInt(matchedLevel.finish, 10);
      maskBookId = storyItem.bookId ?? null;
    } else {
      const selectedCover = selectedItems[0] as { start?: string; finish?: string };
      startPage = parseInt(selectedCover.start || '', 10);
      finishPage = parseInt(selectedCover.finish || '', 10);

      if (cloneResourceType === resource_types.AP) {
        const ap = selectedConfig?.selectedCategoryAP as { chapter_ids?: string | number } | undefined;
        maskBookId = ap?.chapter_ids ?? null;
      } else {
        maskBookId = RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
      }
    }
    if (!startPage || !finishPage || isNaN(startPage) || isNaN(finishPage) || !maskBookId) {
      return [];
    }

    const selectedBook = booksAllList.find((b) => {
      const bookIdNum = Number(maskBookId);
      const bIdNum = Number(b.id);
      return bIdNum === bookIdNum || String(b.id) === String(maskBookId) || b.id === maskBookId;
    });

    if (!selectedBook || !selectedBook.pages) return [];

    const pagesInRange = selectedBook.pages.filter((p: unknown) => {
      const pid = typeof p === 'number' ? p : (p as { pageId?: number; id?: number }).pageId || (p as { id?: number }).id;
      const pageNum = parseInt(String(pid), 10);
      return pageNum >= startPage! && pageNum <= finishPage!;
    });

    return pagesInRange.map((p: unknown) => {
      const pageId = typeof p === 'number' ? p : (p as { pageId?: number; id?: number }).pageId || (p as { id?: number }).id;
      const pageNum = parseInt(String(pageId), 10);
      const maskId = pageNum - startPage! + 1;
      return {
        value: pageId!.toString(),
        label: maskId.toString(),
        maskId: maskId.toString(),
      };
    });
  }, [selectedItems, cloneResourceType, booksAllList, selectedConfig]);

  React.useEffect(() => {
    if (open && assignment) {
      setCloneResourceType(currentResourceType);
      setTargetBook((assignment.chapter || assignment.bookId || '') as string | number);
      setTargetPage('');
      setMode('copy');
      setSelectedItems([]);
      setSelectedConfig({});
      setSelectedMaskId('');
    }
  }, [open, assignment?.id, currentResourceType]);

  const handleClose = () => {
    setCloneResourceType(currentResourceType);
    setTargetBook((assignment?.chapter || assignment?.bookId || '') as string | number);
    setTargetPage('');
    setMode('copy');
    setSelectedItems([]);
    setSelectedConfig({});
    setSelectedMaskId('');
    onClose();
  };

  const isFormValid = () => {
    if (cloneResourceType === 'ebook') {
      return Boolean(targetBook && targetPage !== '' && targetPage !== undefined);
    }
    if (!selectedItems || selectedItems.length === 0) {
      return false;
    }
    if (needsMaskIdDropdown() && !selectedMaskId) {
      return false;
    }
    const bk = getBookIdForResourceType();
    const pg = getMaskIdFromSelection();
    return Boolean(bk && pg !== '' && pg !== undefined && pg !== null);
  };

  const handleSubmit = async () => {
    if (!assignment?.id) {
      showAlert('Missing assignment', 'error');
      return;
    }
    const copyToPageTypeIds = ['activity', 'quiz', 'worksheet'];
    if (!adminMode && assignment.typeId && !copyToPageTypeIds.includes(assignment.typeId)) {
      showAlert('Copy to page is not available for this assignment type', 'error');
      return;
    }
    if (cloneResourceType === 'ebook') {
      if (!targetBook || targetPage === '' || targetPage === undefined) {
        showAlert('Select book and page', 'error');
        return;
      }
    } else {
      if (!selectedItems || selectedItems.length === 0) {
        showAlert('Please select a resource', 'error');
        return;
      }
      if (needsMaskIdDropdown() && !selectedMaskId) {
        showAlert('Please select a mask ID', 'error');
        return;
      }
    }

    const destChapter = getBookIdForResourceType();
    const destPage = getMaskIdFromSelection();
    if (!destChapter || destPage === '' || destPage === undefined || destPage === null) {
      showAlert('Missing book_id or page_id', 'error');
      return;
    }

    setLoading(true);
    try {
      if (adminMode) {
        if (!currentUser?.id) {
          showAlert('Missing user', 'error');
          setLoading(false);
          return;
        }
        if (isWorksheetAssignment) {
          const out = await dispatch(
            handleCloneWorksheetAssignmentToPage(
              String(destPage),
              String(destChapter),
              assignment.id,
              currentUser.id,
              null,
              effectiveMode === 'copy' ? { id: assignment.id } : null,
              effectiveMode
            )
          );
          const outOk =
            out &&
            typeof out === 'object' &&
            'id' in out &&
            out.id &&
            !('error' in out && (out as { error?: string }).error);
          if (outOk) {
            showAlert(
              effectiveMode === 'move'
                ? 'Grammar drill moved to selected page'
                : 'Grammar drill copied to selected page',
              'success'
            );
            handleClose();
            onSuccess?.(destChapter);
          } else {
            showAlert(
              effectiveMode === 'move' ? 'Could not move grammar drill' : 'Could not copy grammar drill',
              'error'
            );
          }
        } else {
          const adminKind = assignment.typeId === 'activity' ? 'activity' : 'quiz';
          const out = await dispatch(
            handleAdminCopyOrMoveAssignment({
              operation: effectiveMode,
              typeId: adminKind,
              assignment_id: assignment.id,
              chapter: destChapter,
              page: String(destPage),
              bookId: destChapter,
              acting_user_id: currentUser.id,
            })
          );
          if (out && typeof out === 'object' && 'error' in out && out.error) {
            showAlert(String(out.error), 'error');
          } else if (out && typeof out === 'object' && 'id' in out && out.id) {
            showAlert(effectiveMode === 'move' ? 'Moved to selected page' : 'Copied to selected page', 'success');
            handleClose();
            onSuccess?.(destChapter);
          } else {
            showAlert(effectiveMode === 'move' ? 'Could not move' : 'Could not copy', 'error');
          }
        }
      } else if (effectiveMode === 'copy') {
        const created = await dispatch(
          isWorksheetAssignment
            ? handleCloneWorksheetAssignmentToPage(
                String(destPage),
                String(destChapter),
                assignment.id,
                currentUser?.id || '',
                null,
                { id: assignment.id },
                'copy'
              )
            : handleCloneCustomAssignment(
                String(destPage),
                String(destChapter),
                assignment.id,
                currentUser?.id || '',
                null,
                {
                  id: assignment.id
                },
                "copy",
                currentUser?.role
              )
        );
        const createdOk =
          created &&
          typeof created === 'object' &&
          'id' in created &&
          created.id &&
          !('error' in created && (created as { error?: string }).error);
        if (createdOk) {
          showAlert(
            isWorksheetAssignment ? 'Grammar drill copied to selected page' : 'Activity copied to selected page',
            'success'
          );
          handleClose();
          onSuccess?.(destChapter);
        } else {
          showAlert(isWorksheetAssignment ? 'Could not copy grammar drill' : 'Could not copy activity', 'error');
        }
      } else {
        if (isWorksheetAssignment) {
          const moved = await dispatch(
            handleCloneWorksheetAssignmentToPage(
              String(destPage),
              String(destChapter),
              assignment.id,
              currentUser?.id || '',
              null,
              null,
              'move'
            )
          );
          const movedOk =
            moved &&
            typeof moved === 'object' &&
            'id' in moved &&
            moved.id &&
            !('error' in moved && (moved as { error?: string }).error);
          if (movedOk) {
            if (is_assigned) {
              await Promise.allSettled([
                dispatch(handleListClassActivities(currentClass.id)),
                dispatch(handleListClassQuizzes(currentClass.id)),
              ]);
            }
            showAlert('Grammar drill moved to selected page', 'success');
            handleClose();
            onSuccess?.(destChapter);
          } else {
            showAlert('Could not move grammar drill', 'error');
          }
        } else {
          const updated = await dispatch(
            handleUpdateCustomAssignment(
              {
                id: assignment.id,
                relocate: true,
                chapter: String(destChapter),
                page: String(destPage),
                bookId: String(destChapter),
              },
              false,
              false
            )
          );
          if (updated && typeof updated === 'object' && 'error' in updated && updated.error) {
            showAlert(String(updated.error), 'error');
          } else if (updated && typeof updated === 'object' && 'id' in updated && updated.id) {
            if (is_assigned) {
              await Promise.allSettled([
                dispatch(handleListClassActivities(currentClass.id)),
                dispatch(handleListClassQuizzes(currentClass.id)),
              ]);
            }
            showAlert('Activity moved to selected page', 'success');
            handleClose();
            onSuccess?.(destChapter);
          } else {
            showAlert('Could not move activity', 'error');
          }
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Request failed';
      showAlert(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!assignment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <p style={{ marginTop: 16, marginBottom: 16, fontSize: 14, color: '#666' }}>
          Select resource type and destination for &quot;
          {assignment.displayName || (isWorksheetAssignment ? 'Grammar drill' : 'Activity')}&quot;
        </p>
        {(adminMode || isCustom || isWorksheetAssignment) && (
          <FormControl component="fieldset" style={{ marginBottom: 16 }}>
            <RadioGroup
              style={{ display: 'flex', flexDirection: 'row', gap: 10 }}
              row
              value={mode}
              onChange={(e) => handleModeChange(e.target.value as Mode)}
            >
              <FormControlLabel value="copy" control={<Radio />} label="Copy (keep original)" />
              {canShowMoveOption && (
                <FormControlLabel value="move" control={<Radio />} label="Move (change location)" />
              )}
            </RadioGroup>
          </FormControl>
        )}
        <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
          <InputLabel>Resource Type</InputLabel>
          <Select
            value={cloneResourceType}
            label="Resource Type"
            onChange={(e) => handleResourceTypeChange(e.target.value)}
          >
            {map(dropdownForResourceType, (element, i) => (
              <MenuItem key={i} value={element?.value}>
                {element?.label}
              </MenuItem>
            ))}
            <MenuItem value="ebook">Ebook</MenuItem>
          </Select>
        </FormControl>

        {cloneResourceType === 'ebook' ? (
          <>
            <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
              <InputLabel>Book</InputLabel>
              <Select
                value={targetBook}
                label="Book"
                onChange={(e) => {
                  setTargetBook(e.target.value as string | number);
                  setTargetPage('');
                }}
              >
                {availableBooksForTarget.map((bk) => (
                  <MenuItem key={bk.id} value={bk.id}>
                    {bk.description || bk.title || bk.name || `Book ${bk.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Page</InputLabel>
              <Select
                value={targetPage}
                label="Page"
                onChange={(e) => setTargetPage(e.target.value as string | number)}
              >
                {pagesForTargetBook.map((p) => (
                  <MenuItem key={String(p.value)} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            <ResourceTypeSelector
              data={selectedItems}
              type={cloneResourceType}
              config={selectedConfig}
              handleSubmit={(item, additionalResource) => {
                setSelectedItems(item);
                setSelectedConfig({
                  ...selectedConfig,
                  ...(additionalResource && cloneResourceType === resource_types.AP && {
                    selectedCategoryAP: additionalResource,
                  }),
                  ...(additionalResource && cloneResourceType === resource_types.resource && {
                    linkResource: additionalResource,
                  }),
                  ...(additionalResource && cloneResourceType === resource_types.story && {
                    level: additionalResource,
                  }),
                });
              }}
              pageId={contextPageId}
            />
            {selectedItems && selectedItems.length > 0 && (
              <>
                {needsMaskIdDropdown() && (
                  <FormControl variant="outlined" fullWidth style={{ marginTop: 16 }}>
                    <InputLabel>Page Id</InputLabel>
                    <Select
                      value={selectedMaskId}
                      label="Page Id"
                      onChange={(e) => setSelectedMaskId(e.target.value)}
                    >
                      {getMaskIdOptionsForSelectedCover.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <div style={{ marginTop: 16, padding: 12, border: '1px solid #ddd', borderRadius: 4 }}>
                  <p style={{ marginBottom: 8, fontWeight: 'bold' }}>Selected Resource:</p>
                  {isBookUnitType() || cloneResourceType === resource_types.AP ? (
                    <p style={{ fontSize: 14 }}>
                      {(selectedItems[0] as { shortName?: string; displayName?: string })?.shortName ||
                        (selectedItems[0] as { displayName?: string })?.displayName}
                    </p>
                  ) : cloneResourceType === resource_types.resource ? (
                    <p style={{ fontSize: 14 }}>
                      {(selectedItems[0] as { displayName?: string })?.displayName}
                    </p>
                  ) : cloneResourceType === resource_types.story ? (
                    <p style={{ fontSize: 14 }}>{(selectedItems[0] as { title?: string })?.title}</p>
                  ) : (
                    <p style={{ fontSize: 14 }}>
                      {(selectedItems[0] as { shortName?: string; displayName?: string })?.shortName ||
                        (selectedItems[0] as { displayName?: string })?.displayName}
                    </p>
                  )}
                  {!needsMaskIdDropdown() && (
                    <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Mask ID: {String(getMaskIdFromSelection() ?? '')}
                    </p>
                  )}
                  {needsMaskIdDropdown() && selectedMaskId && (
                    <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Selected Page ID: {selectedMaskId}
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !isFormValid()}>
          {loading ? <Loading size={15} white /> : effectiveMode === 'move' ? 'Move' : 'Copy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneActivityToPageModal;
